import { DateUtils } from '_lib/utils/date.utils';

import {
  createApiResource,
  ResourceId,
  CreateResource,
  PatchResource,
  GetByIdResource,
  UnpagedListResource,
  ListResource,
  UnpagedListHookResult,
  ListHookResult,
  QueryKey,
  ListOptions
} from '../apiResourceFactory';
import { ServiceTitanAPI } from '../apiService';
import {
  ServiceTitanCustomerQueryParams,
  Customer,
  CustomerCreateDTO,
  CustomerCreateResponseDTO,
  CustomerDetailDTO,
  CustomerPatchDTO,
  CustomerSearchDTO,
  CustomerMembership,
  ServiceTitanJobHistoryItem,
  ServiceTitanJobHistoryQueryParams,
  ServiceTitanScheduledAppointment,
  ServiceTitanScheduledAppointmentQueryParams,
  TimezoneAbbreviationEnum,
  ServiceTitanAppointment,
  ServiceTitanAppointmentQueryParams
} from '../apiTypes.shim';
import {
  ApiResourceWithNotes,
  ApiResourceWithContacts,
  createNotesResource,
  createContactsResource
} from '../stApiResourceFactory';

export type ServiceTitanCustomerAppointmentResource = ListResource<
  ServiceTitanAppointment,
  ServiceTitanAppointmentQueryParams
> & {
  useListCached: (options?: ListOptions<ServiceTitanAppointmentQueryParams>) => ListHookResult<ServiceTitanAppointment>;
};

const createServiceTitanCustomerAppointmentResource = (
  companyId: string,
  providerCustomerId: string
): ServiceTitanCustomerAppointmentResource => {
  const customerAppointmentsUri = `${companyId}/customers/${providerCustomerId}/appointments`;

  const { useList, list, listQueryKey } = createApiResource(ServiceTitanAPI, customerAppointmentsUri);

  return {
    useList,
    list,
    listQueryKey,
    useListCached: (options) => useList(options, { refetchOnMount: false })
  };
};

type ServiceTitanCustomerScheduledAppointmentResource = UnpagedListResource<
  ServiceTitanScheduledAppointment,
  ServiceTitanScheduledAppointmentQueryParams
> & {
  unpagedListCached: (timezone: TimezoneAbbreviationEnum) => Promise<ServiceTitanScheduledAppointment[]>;
  cachedQueryKey: (timezone: TimezoneAbbreviationEnum) => QueryKey;
  useUnpagedListCached: (timezone: TimezoneAbbreviationEnum) => UnpagedListHookResult<ServiceTitanScheduledAppointment>;
};

const createServiceTitanCustomerScheduledAppointmentResource = (
  companyId: string,
  providerCustomerId: string
): ServiceTitanCustomerScheduledAppointmentResource => {
  const customerScheduledAppointmentsUri = `${companyId}/customers/${providerCustomerId}/scheduled-appointments`;

  const { useUnpagedList, unpagedList, unpagedListQueryKey } = createApiResource(
    ServiceTitanAPI,
    customerScheduledAppointmentsUri
  );

  const getCacheParams = (
    timezone: TimezoneAbbreviationEnum
  ): Pick<ServiceTitanScheduledAppointmentQueryParams, 'startsOnOrAfter'> => ({
    startsOnOrAfter: DateUtils.toStartOf(new Date(), 'day', { timezone }).toISOString()
  });

  return {
    useUnpagedList,
    unpagedList,
    unpagedListQueryKey,
    cachedQueryKey: (timezone) => unpagedListQueryKey({ params: getCacheParams(timezone) }),
    unpagedListCached: async (timezone) => {
      const res = await unpagedList<ServiceTitanScheduledAppointment>({ params: getCacheParams(timezone) });
      return res.results;
    },
    useUnpagedListCached: (timezone) => useUnpagedList({ params: getCacheParams(timezone) }, { refetchOnMount: false })
  };
};

export type ServiceTitanCustomerJobHistoryResource = ListResource<
  ServiceTitanJobHistoryItem,
  ServiceTitanJobHistoryQueryParams
> & {
  useListCached: () => ListHookResult<ServiceTitanJobHistoryItem>;
};

const createServiceTitanCustomerJobHistoryResource = (
  companyId: string,
  providerCustomerId: string
): ServiceTitanCustomerJobHistoryResource => {
  const customerJobHistoryUri = `${companyId}/customers/${providerCustomerId}/job-history-items`;

  const { useList, list, listQueryKey } = createApiResource(ServiceTitanAPI, customerJobHistoryUri);

  return {
    useList,
    list,
    listQueryKey,
    useListCached: () => useList({}, { refetchOnMount: false })
  };
};

interface ServiceTitanCustomerMembershipResource extends UnpagedListResource<CustomerMembership> {
  useUnpagedListCached: () => UnpagedListHookResult<CustomerMembership>;
}

const createServiceTitanCustomerMembershipResource = (
  companyId: string,
  providerCustomerId: string
): ServiceTitanCustomerMembershipResource => {
  const customerMembershipUri = `${companyId}/customers/${providerCustomerId}/memberships`;

  const { useUnpagedList, unpagedList, unpagedListQueryKey } = createApiResource(
    ServiceTitanAPI,
    customerMembershipUri
  );

  return {
    useUnpagedList,
    unpagedList,
    unpagedListQueryKey,
    useUnpagedListCached: () => useUnpagedList({}, { refetchOnMount: false })
  };
};

export interface ServiceTitanCustomerResource
  extends CreateResource<CustomerCreateDTO, CustomerCreateResponseDTO>,
    PatchResource<CustomerPatchDTO, Customer>,
    ListResource<Customer, ServiceTitanCustomerQueryParams>,
    GetByIdResource<CustomerDetailDTO>,
    ApiResourceWithNotes,
    ApiResourceWithContacts {
  memberships: (providerCustomerId: string) => ServiceTitanCustomerMembershipResource;
  search: UnpagedListResource<CustomerSearchDTO, ServiceTitanCustomerQueryParams>['unpagedList'];
  appointments: (providerCustomerId: string) => ServiceTitanCustomerAppointmentResource;
  scheduledAppointments: (providerCustomerId: string) => ServiceTitanCustomerScheduledAppointmentResource;
  jobHistory: (providerCustomerId: string) => ServiceTitanCustomerJobHistoryResource;
}

export const createServiceTitanCustomerResource = (companyId: string): ServiceTitanCustomerResource => {
  const stCustomerResourceUri = `${companyId}/customers`;

  const { create, useCreate, list, useList, getById, useGetById, patch, usePatch, listQueryKey, getByIdQueryKey } =
    createApiResource(ServiceTitanAPI, stCustomerResourceUri);

  return {
    list,
    useList,
    getById,
    useGetById,
    create,
    useCreate,
    patch,
    usePatch,
    contacts: (providerCustomerId: ResourceId) =>
      createContactsResource(ServiceTitanAPI, `${stCustomerResourceUri}/${providerCustomerId}`),
    memberships: (providerCustomerId: string) =>
      createServiceTitanCustomerMembershipResource(companyId, providerCustomerId),
    notes: (providerCustomerId: ResourceId) =>
      createNotesResource(ServiceTitanAPI, `${stCustomerResourceUri}/${providerCustomerId}`),
    search: async (options) => {
      const res = await ServiceTitanAPI.get(`${stCustomerResourceUri}/search`, options);
      return res.data;
    },
    appointments: (providerCustomerId: ResourceId) =>
      createServiceTitanCustomerAppointmentResource(companyId, providerCustomerId),
    scheduledAppointments: (providerCustomerId: ResourceId) =>
      createServiceTitanCustomerScheduledAppointmentResource(companyId, providerCustomerId),
    jobHistory: (providerCustomerId: ResourceId) =>
      createServiceTitanCustomerJobHistoryResource(companyId, providerCustomerId),
    listQueryKey,
    getByIdQueryKey
  };
};
